import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import swal from 'sweetalert';
import Sidebar from './Sidebar';
import Loader from '../layout/loader';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import './adminRoutes.css';

function SellerApplications() {
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const initialStatusParam = searchParams.get('status');
    const initialFilter = initialStatusParam
        ? (initialStatusParam.charAt(0).toUpperCase() + initialStatusParam.slice(1).toLowerCase())
        : 'All';

    const [loading, setLoading] = useState(true);
    const [profiles, setProfiles] = useState([]);
    const [filterStatus, setFilterStatus] = useState(initialFilter);
    const [searchTerm, setSearchTerm] = useState('');
    const [actionLoadingId, setActionLoadingId] = useState(null);

    useEffect(() => {
        const queryStatus = searchParams.get('status');
        if (queryStatus) {
            setFilterStatus(queryStatus.charAt(0).toUpperCase() + queryStatus.slice(1).toLowerCase());
        }
    }, [searchParams]);

    const fetchProfiles = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get('/api/admin/vendors');
            setProfiles(data.profiles || []);
        } catch (error) {
            swal({
                title: "Error",
                text: error.response?.data?.msg || "Failed to load seller applications.",
                icon: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfiles();
    }, []);

    const handleStatusUpdate = async (id, newStatus, sellerName) => {
        let rejectionReason = '';
        if (newStatus === 'rejected') {
            const reason = await swal({
                title: "Reject Application?",
                text: `Provide a reason for rejecting ${sellerName}'s application (optional):`,
                content: "input",
                buttons: ["Cancel", "Confirm Reject"],
                dangerMode: true,
            });
            if (reason === null) return;
            rejectionReason = reason;
        } else {
            const willProceed = await swal({
                title: `${newStatus === 'approved' ? 'Approve' : 'Suspend'} Seller?`,
                text: `Are you sure you want to change ${sellerName}'s status to ${newStatus}?`,
                icon: newStatus === 'approved' ? "info" : "warning",
                buttons: ["Cancel", "Confirm"],
                dangerMode: newStatus !== 'approved',
            });
            if (!willProceed) return;
        }

        try {
            setActionLoadingId(id);
            const { data } = await axios.put(`/api/admin/vendor/${id}/status`, {
                status: newStatus,
                rejectionReason
            });
            swal({
                title: "Success",
                text: data.message || `Seller status updated to ${newStatus}.`,
                icon: "success"
            });
            fetchProfiles();
        } catch (error) {
            swal({
                title: "Action Failed",
                text: error.response?.data?.msg || "Unable to update vendor status.",
                icon: "error"
            });
        } finally {
            setActionLoadingId(null);
        }
    };

    const filteredProfiles = useMemo(() => {
        return profiles.filter((p) => {
            const matchesStatus = filterStatus === 'All' || p.status === filterStatus.toLowerCase();
            const term = searchTerm.toLowerCase();
            const matchesSearch = !searchTerm ||
                (p.businessName && p.businessName.toLowerCase().includes(term)) ||
                (p.user?.firstName && p.user.firstName.toLowerCase().includes(term)) ||
                (p.user?.lastName && p.user.lastName.toLowerCase().includes(term)) ||
                (p.email && p.email.toLowerCase().includes(term)) ||
                (p.phone && p.phone.includes(term));
            return matchesStatus && matchesSearch;
        });
    }, [profiles, filterStatus, searchTerm]);

    const counts = useMemo(() => {
        return {
            all: profiles.length,
            pending: profiles.filter(p => p.status === 'pending').length,
            approved: profiles.filter(p => p.status === 'approved').length,
            rejected: profiles.filter(p => p.status === 'rejected').length,
            suspended: profiles.filter(p => p.status === 'suspended').length,
        };
    }, [profiles]);

    return (
        <Fragment>
            <div className="dashboard-container">
                <Sidebar />
                <main className="main-container admin-dashboard-main">
                    {/* Header */}
                    <section className="admin-dashboard-hero">
                        <div>
                            <span className="admin-dashboard-eyebrow">MARKETPLACE OPERATIONS</span>
                            <h1>Seller Applications &amp; Vendors</h1>
                            <p>Review incoming seller applications, verify credentials, and manage merchant statuses.</p>
                        </div>
                        <button type="button" className="admin-refresh-button" onClick={fetchProfiles} disabled={loading}>
                            <RefreshIcon />
                            <span>Refresh</span>
                        </button>
                    </section>

                    {/* Filter Bar */}
                    <div className="admin-filter-bar" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', margin: '20px 0', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {[
                                { label: 'All', count: counts.all },
                                { label: 'Pending', count: counts.pending },
                                { label: 'Approved', count: counts.approved },
                                { label: 'Rejected', count: counts.rejected },
                                { label: 'Suspended', count: counts.suspended },
                            ].map((tab) => (
                                <button
                                    key={tab.label}
                                    type="button"
                                    onClick={() => setFilterStatus(tab.label)}
                                    className={`admin-filter-pill ${filterStatus === tab.label ? 'active' : ''}`}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '99px',
                                        border: '1.5px solid #e0e4ea',
                                        background: filterStatus === tab.label ? '#111315' : '#ffffff',
                                        color: filterStatus === tab.label ? '#ffffff' : '#4f5661',
                                        fontWeight: 800,
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        transition: 'all 160ms ease'
                                    }}
                                >
                                    {tab.label} ({tab.count})
                                </button>
                            ))}
                        </div>

                        <div style={{ position: 'relative', minWidth: '260px' }}>
                            <SearchIcon style={{ position: 'absolute', left: '10px', top: '10px', color: '#888', fontSize: '20px' }} />
                            <input
                                type="text"
                                placeholder="Search by name, email, phone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '9px 12px 9px 36px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #e0e4ea',
                                    fontSize: '13.5px',
                                    outline: 'none'
                                }}
                            />
                        </div>
                    </div>

                    {/* Content */}
                    {loading ? (
                        <Loader />
                    ) : filteredProfiles.length === 0 ? (
                        <div className="admin-panel" style={{ textAlign: 'center', padding: '60px 20px' }}>
                            <StorefrontIcon style={{ fontSize: '48px', color: '#c0c5cc', marginBottom: '12px' }} />
                            <h3 style={{ color: '#111315', fontWeight: 800, margin: '0 0 6px' }}>No seller applications found</h3>
                            <p style={{ color: '#737a84', fontSize: '14px', margin: 0 }}>There are no applications matching the selected status or search term.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '18px' }}>
                            {filteredProfiles.map((p) => (
                                <div key={p._id} className="admin-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '22px' }}>
                                    <div>
                                        {/* Card Top */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                                            <div>
                                                <div style={{ fontSize: '11px', fontWeight: 900, color: '#a85050', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                                    {p.businessType || 'INDIVIDUAL'}
                                                </div>
                                                <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#111315', margin: '4px 0 2px' }}>
                                                    {p.businessName}
                                                </h3>
                                                <div style={{ fontSize: '13px', color: '#6a6864' }}>
                                                    Applicant: <strong>{p.user?.firstName ? `${p.user.firstName} ${p.user.lastName || ''}` : 'Seller'}</strong>
                                                </div>
                                            </div>
                                            <span
                                                className={`status-pill ${p.status}`}
                                                style={{
                                                    fontSize: '11px',
                                                    fontWeight: 900,
                                                    textTransform: 'uppercase',
                                                    padding: '4px 10px',
                                                    borderRadius: '6px',
                                                    background: p.status === 'approved' ? '#dcfce7' : p.status === 'pending' ? '#fef3c7' : '#fee2e2',
                                                    color: p.status === 'approved' ? '#166534' : p.status === 'pending' ? '#92400e' : '#991b1b',
                                                }}
                                            >
                                                {p.status}
                                            </span>
                                        </div>

                                        {/* Details */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', color: '#4f5661', marginBottom: '18px', background: '#fbfaf8', padding: '12px 14px', borderRadius: '8px', border: '1px solid #edf0f4' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <EmailIcon style={{ fontSize: '16px', color: '#888' }} />
                                                <span>{p.email || p.user?.email || '—'}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <PhoneIcon style={{ fontSize: '16px', color: '#888' }} />
                                                <span>{p.phone || p.user?.number || '—'}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                                <LocationOnIcon style={{ fontSize: '16px', color: '#888', marginTop: '2px' }} />
                                                <span>
                                                    {p.address ? `${p.address.street || ''}, ${p.address.city || ''}, ${p.address.state || ''} - ${p.address.pincode || ''}` : 'No address specified'}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '11.5px', color: '#8c939f', marginTop: '4px' }}>
                                                Submitted: {p.submittedAt ? new Date(p.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                            </div>
                                            {p.rejectionReason && (
                                                <div style={{ background: '#fee2e2', color: '#991b1b', padding: '6px 10px', borderRadius: '4px', fontSize: '12px' }}>
                                                    Reason: {p.rejectionReason}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #edf0f4', paddingTop: '14px' }}>
                                        <Link
                                            to={`/Admin/vendors/${p._id}`}
                                            style={{
                                                width: '100%',
                                                background: '#111315',
                                                color: '#ffffff',
                                                textDecoration: 'none',
                                                borderRadius: '6px',
                                                padding: '8px 12px',
                                                fontWeight: 800,
                                                fontSize: '12.5px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <StorefrontIcon style={{ fontSize: '16px' }} /> Vendor 360° Overview
                                        </Link>

                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            {p.status === 'pending' && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleStatusUpdate(p._id, 'approved', p.businessName)}
                                                        disabled={actionLoadingId === p._id}
                                                        style={{
                                                            flex: 1,
                                                            background: '#16a34a',
                                                            color: '#ffffff',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '8px 12px',
                                                            fontWeight: 800,
                                                            fontSize: '12.5px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '6px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <CheckCircleOutlineIcon style={{ fontSize: '16px' }} /> Approve
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleStatusUpdate(p._id, 'rejected', p.businessName)}
                                                        disabled={actionLoadingId === p._id}
                                                        style={{
                                                            flex: 1,
                                                            background: '#fee2e2',
                                                            color: '#dc2626',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '8px 12px',
                                                            fontWeight: 800,
                                                            fontSize: '12.5px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '6px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <HighlightOffIcon style={{ fontSize: '16px' }} /> Reject
                                                    </button>
                                                </>
                                            )}

                                            {p.status === 'approved' && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleStatusUpdate(p._id, 'suspended', p.businessName)}
                                                    disabled={actionLoadingId === p._id}
                                                    style={{
                                                        width: '100%',
                                                        background: '#fff3cd',
                                                        color: '#856404',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '8px 12px',
                                                        fontWeight: 800,
                                                        fontSize: '12.5px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <BlockIcon style={{ fontSize: '16px' }} /> Suspend Account
                                                </button>
                                            )}

                                            {(p.status === 'rejected' || p.status === 'suspended') && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleStatusUpdate(p._id, 'approved', p.businessName)}
                                                    disabled={actionLoadingId === p._id}
                                                    style={{
                                                        width: '100%',
                                                        background: '#16a34a',
                                                        color: '#ffffff',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '8px 12px',
                                                        fontWeight: 800,
                                                        fontSize: '12.5px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <CheckCircleOutlineIcon style={{ fontSize: '16px' }} /> Reinstate &amp; Approve
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </Fragment>
    );
}

export default SellerApplications;
